import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTileColours } from './AddRelativePanel/tileTheme';

/**
 * The risk tools of the pedigree's Risk scores section, as a grid of tiles
 * (Tim, QA 2026-10-16): one wide button per tool read as clunky with three, and
 * would with five or six.
 *
 * Drawn as the add-relative tiles are (AddRelativePanel/RelativeTile.jsx, the
 * colours from its tileTheme): light and neutral at rest, a faint accent tint on
 * hover. Each tile carries its tool's icon on an accent-tinted square, as the
 * form builder's tool tiles do, its name, and a line on what it calculates.
 *
 * @param {object} props
 * @param {Array<{
 *   key: string,
 *   label: string,
 *   caption: string,
 *   Icon: React.ElementType,
 *   onClick: Function,
 *   disabled?: boolean,
 *   disabledReason?: string,
 *   vaAnchor?: string
 * }>} props.tools  label, caption and disabledReason already translated
 */
export default function RiskToolTiles({ tools }) {
  const c = useTileColours();
  if (!tools?.length) return null;
  return (
    <Box
      data-testid="risk-tool-tiles"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 1
      }}
    >
      {tools.map(
        ({
          key,
          label,
          caption,
          Icon,
          onClick,
          disabled,
          disabledReason,
          vaAnchor
        }) => {
          const tile = (
            <Box
              component="button"
              type="button"
              key={key}
              data-testid={`risk-tool-${key}`}
              data-va-anchor={vaAnchor}
              aria-label={
                disabledReason
                  ? `${label}, ${caption}, ${disabledReason}`
                  : `${label}, ${caption}`
              }
              disabled={disabled}
              onClick={onClick}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: 104,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.75,
                px: 1,
                py: 1.25,
                font: 'inherit',
                textAlign: 'center',
                cursor: disabled ? 'default' : 'pointer',
                borderRadius: 1.5,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                color: disabled ? 'text.secondary' : c.restText,
                transition:
                  'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
                ...(disabled
                  ? {}
                  : {
                      '&:hover': {
                        bgcolor: c.hoverTint,
                        borderColor: c.hoverBorder,
                        color: c.hoverText
                      }
                    }),
                '&:focus-visible': {
                  outline: 2,
                  outlineColor: c.accent,
                  outlineOffset: 2
                }
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  bgcolor: disabled ? 'action.hover' : alpha(c.accent, 0.12),
                  color: disabled ? 'text.disabled' : c.hoverGlyph
                }}
              >
                <Icon fontSize="small" />
              </Box>
              <Typography
                component="span"
                sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}
              >
                {label}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  lineHeight: 1.25,
                  color: 'text.secondary',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {caption}
              </Typography>
            </Box>
          );
          // MUI cannot attach a tooltip to a disabled button.
          return disabledReason ? (
            <Tooltip key={key} title={disabledReason} placement="top">
              <span style={{ display: 'block', height: '100%' }}>{tile}</span>
            </Tooltip>
          ) : (
            tile
          );
        }
      )}
    </Box>
  );
}
